// SqlResolverService — one instance per (connectionId, engine). Hybrid
// external surface (SR1): functional factory with a document cache, a
// SchemaSnapshot cache, and DDL invalidation hooked into the existing
// `useQueryExecution.ts` seam via `ddlNotifier`.
//
// This slice ships the connection-lifecycle methods + the read-only Resolver
// API bound to the service's caches. Incremental parse lands in the next
// slice (AST Cache + Incremental Parse); full re-parse is the current
// behaviour for `onTextChange`.
//
// Threading assumption: single-renderer single-threaded (Electron renderer).
// Concurrent schema pulls are serialized via an in-flight promise guard.

import type {
  ParseOptions,
  QualifiedName,
  SqlEngine,
  IdentifierRef,
  StatementInfo,
} from '../../sql-grammar/model';
import type {
  Column,
  DdlEdit,
  DdlStructure,
  DottedBase,
  IdentifierBinding,
  JoinCandidate,
  ModifiedDdlStatement,
  ModifiedStatement,
  OperandType,
  ParsedBuffer,
  ResolverPositionKind,
  QueryStructure,
  Routine,
  Scope,
  SchemaSnapshot,
  ScriptScope,
  SqlPosition,
  StructuralEdit,
  Table,
} from '../types';

import { classifyPosition } from '../classify/classifyPosition';
import { resolveScope } from '../scope/resolveScope';
import { resolveDottedBase } from '../dotted/resolveDottedBase';
import { resolveIdentifier } from '../identifier/resolveIdentifier';
import { suggestColumnsInScope } from '../suggest/suggestColumns';
import { suggestTablesInScope } from '../suggest/suggestTables';
import { suggestJoinCandidates } from '../suggest/suggestJoinCandidates';
import { suggestFunctionsInScope } from '../suggest/suggestFunctions';
import { extractQueryStructure } from '../structure/extractQueryStructure';
import { applyStructuralEdit } from '../edit/applyStructuralEdit';
import { foldConditionPairs } from '../edit/foldConditionPairs';
import {
  emitSource,
  type EmitSourceInput,
  type EmitSourceOptions,
} from '../edit/emitSource';
import { extractDdlStructure } from '../ddl/extractDdlStructure';
import { applyDdlEdit, type ApplyDdlEditOptions } from '../ddl/apply/applyDdlEdit';
import { suggestOperatorsForOperand } from '../json/operators/suggestOperators';
import type { Operator } from '../json/operators/types';

import { createDocumentCache, type BuildParsedDocumentFn, type DocumentCache } from './documentCache';
import type { TextEdit } from './parseIncremental';
import type { PullSchemaSnapshotFn } from './schemaCacheAdapter';
import { subscribeToDdl } from './ddlNotifier';

export type CreateResolverServiceParams = {
  readonly connectionId: string;
  readonly engineId: SqlEngine;
  readonly buildParsedDocument: BuildParsedDocumentFn;
  readonly pullSchemaSnapshot?: PullSchemaSnapshotFn;
  readonly maxCachedDocs?: number;
  /**
   * Two adjacent AND-joined WHERE / HAVING conditions whose text this accepts
   * are ONE condition in every structure the service hands out and plans
   * edits on (`foldConditionPairs`) — the Visual Query Editor's older
   * unparenthesised chip filters. Omitted: structures are the parse as is.
   */
  readonly foldConditionPair?: (text: string) => boolean;
};

export type OpenDocumentParams = {
  readonly documentId: string;
  readonly text: string;
  readonly options: ParseOptions;
  readonly scriptScope?: ScriptScope;
};

export type SqlResolverService = {
  readonly connectionId: string;
  readonly engineId: SqlEngine;

  // Lifecycle
  readonly onOpenDocument: (params: OpenDocumentParams) => Promise<ParsedBuffer>;
  readonly onTextChange: (documentId: string, newText: string) => Promise<ParsedBuffer>;
  readonly onApplyEdits: (documentId: string, edits: readonly TextEdit[]) => Promise<ParsedBuffer>;
  readonly onCloseDocument: (documentId: string) => void;
  readonly onDDL: () => void;
  readonly onDisconnect: () => void;

  // Snapshot management
  readonly refreshSchemaSnapshot: (database?: string) => Promise<SchemaSnapshot | undefined>;
  readonly getSchemaSnapshot: () => SchemaSnapshot | undefined;
  readonly setSchemaSnapshot: (snapshot: SchemaSnapshot | undefined) => void;
  /**
   * Message of the latest schema pull when it REJECTED, cleared by the next
   * successful pull — lets a surface waiting on the snapshot say the load
   * failed instead of showing "loading" forever.
   */
  readonly getSchemaPullError: () => string | undefined;

  // Introspection
  readonly getParsedBuffer: (documentId: string) => ParsedBuffer | undefined;
  readonly documentIds: () => readonly string[];

  // Read-only Resolver API
  readonly classifyPosition: (documentId: string, cursor: SqlPosition) => ResolverPositionKind<string>;
  readonly resolveScope: (documentId: string, cursor: SqlPosition) => Scope;
  readonly resolveDottedBase: (documentId: string, cursor: SqlPosition) => DottedBase | null;
  readonly resolveIdentifier: (documentId: string, node: IdentifierRef) => IdentifierBinding | null;
  readonly suggestColumnsInScope: (scope: Scope, filter?: string) => Column[];
  readonly suggestTablesInScope: (scope: Scope, filter?: string) => Table[];
  readonly suggestJoinCandidates: (scope: Scope, leftTable: QualifiedName) => JoinCandidate[];
  readonly suggestFunctionsInScope: (scope: Scope, filter?: string) => Routine[];

  // Engine + type aware operator suggester. Returns the operator lattice for
  // the operand: JSON operators when the operand is JSON-classified (column-arm
  // or expression-arm), engine-agnostic numeric/text/boolean/null lattices for
  // typed literals, conservative fallback otherwise. PRD: § Slice C2.
  readonly suggestOperatorsForOperand: (operand: OperandType) => readonly Operator[];

  // Edit-side Resolver API (SemanticResolverEdit). Pure: none of these mutate
  // the cached `ParsedBuffer`. The caller (e.g. VQE-Edit) decides when to
  // invalidate via `onTextChange` / `onApplyEdits` after splicing the emitted
  // SQL back into the document.
  readonly extractQueryStructure: (documentId: string, statementId: string) => QueryStructure;
  readonly applyStructuralEdit: (
    documentId: string,
    statementId: string,
    edit: StructuralEdit,
  ) => ModifiedStatement;
  // Range-addressable variants for VQE set-op branch editing (VQE-3 Slice 1).
  // Synthetic sub-statement ids (e.g. `${parent}.sub.START-END`) are NOT
  // present in documentStructure.statements, so the id-based methods above
  // throw for them. These *ForStatement variants accept a caller-supplied
  // StatementInfo with a real byte range and bypass the id lookup entirely —
  // the existing id-based contract is unchanged and still throws for unknown ids.
  // NOTE: the resulting QueryStructure's `ctes` resolve via the synthetic id,
  // which is not in documentStructure.statements, so a set-op branch sees an
  // empty `ctes` — correct for structural edits (range-driven splice; a branch
  // owns no WITH clause, it only references a compound-level CTE) but it does
  // NOT carry parent-CTE binding visibility. Per-branch scope/CTE-reference
  // resolution is the deferred VQE-3 per-branch-scope follow-up.
  readonly extractQueryStructureForStatement: (
    documentId: string,
    statementInfo: StatementInfo,
  ) => QueryStructure;
  readonly applyStructuralEditForStatement: (
    documentId: string,
    statementInfo: StatementInfo,
    edit: StructuralEdit,
  ) => ModifiedStatement;
  // DSE1: emitSource serves both DML (`ModifiedStatement`) and DDL
  // (`ModifiedDdlStatement`) outputs unchanged — the parameter is the
  // structural `EmitSourceInput` shape (`{ plannedEdits }`) so both edit
  // families flow through the same byte-range patcher.
  readonly emitSource: (
    documentId: string,
    modified: EmitSourceInput,
    options?: EmitSourceOptions,
  ) => string;

  // DDL Structural Edit surface (DdlStructuralEdit PRD). Pure parallels of
  // extractQueryStructure / applyStructuralEdit for CREATE TABLE / ALTER
  // TABLE / CREATE INDEX / DROP INDEX. v1 op set locked at column add/drop/
  // rename + index add/drop (DSE3).
  readonly extractDdlStructure: (documentId: string, statementId: string) => DdlStructure;
  readonly applyDdlEdit: (
    documentId: string,
    statementId: string,
    edit: DdlEdit,
    options?: ApplyDdlEditOptions,
  ) => ModifiedDdlStatement;
};

function requireBuffer(cache: DocumentCache, documentId: string): ParsedBuffer {
  const buf = cache.get(documentId);
  if (!buf) {
    throw new Error(`SqlResolverService: no document '${documentId}' is open`);
  }
  return buf;
}

function requireStatement(buffer: ParsedBuffer, statementId: string): StatementInfo {
  const stmt = buffer.documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt) {
    throw new Error(`SqlResolverService: no statement '${statementId}' in document`);
  }
  return stmt;
}

export function createResolverService(params: CreateResolverServiceParams): SqlResolverService {
  const { connectionId, engineId, buildParsedDocument, pullSchemaSnapshot, maxCachedDocs, foldConditionPair } = params;
  const structureOf = (buf: ParsedBuffer, statement: StatementInfo): QueryStructure => {
    const structure = extractQueryStructure(buf, statement);
    return foldConditionPair ? foldConditionPairs(structure, buf.parsedDocument.source, foldConditionPair) : structure;
  };

  const docCache = createDocumentCache(buildParsedDocument, { maxCachedDocs });
  let cachedSnapshot: SchemaSnapshot | undefined;
  const inFlight = new Map<
    string | undefined,
    { promise: Promise<SchemaSnapshot | undefined>; generation: number }
  >();
  let pullGeneration = 0;
  let pullError: string | undefined;
  let disposed = false;

  const unsubscribeDdl = subscribeToDdl(connectionId, () => {
    cachedSnapshot = undefined;
    docCache.rebindSnapshot(undefined);
  });

  function setSnapshot(next: SchemaSnapshot | undefined): void {
    cachedSnapshot = next;
    docCache.rebindSnapshot(next);
  }

  async function refreshSchemaSnapshot(database?: string): Promise<SchemaSnapshot | undefined> {
    if (disposed) return undefined;
    if (!pullSchemaSnapshot) return cachedSnapshot;
    const existing = inFlight.get(database);
    if (existing) return existing.promise;          // per-DB dedup (survives interleaved cross-DB)

    const generation = ++pullGeneration;            // last-*requested* wins
    const promise = (async () => {
      try {
        const next = await pullSchemaSnapshot(database);
        // Apply only if this is still the latest request for ANY DB (and not
        // disposed): an older/slower pull must NOT clobber a newer DB's
        // snapshot — last-requested-wins via the `pullGeneration` check.
        if (!disposed && generation === pullGeneration) {
          setSnapshot(next);
          pullError = undefined;
        }
        return next;
      } catch (error: unknown) {
        if (!disposed && generation === pullGeneration) {
          pullError = error instanceof Error ? error.message : String(error);
        }
        throw error;
      } finally {
        // Evict only our own entry: a newer same-DB pull replacing it bumps
        // the registered generation, so this stale cleanup leaves it intact
        // (don't drop someone else's in-flight pull).
        if (inFlight.get(database)?.generation === generation) inFlight.delete(database);
      }
    })();
    inFlight.set(database, { promise, generation });
    return promise;
  }

  return {
    connectionId,
    engineId,

    async onOpenDocument({ documentId, text, options, scriptScope }) {
      if (disposed) throw new Error('SqlResolverService: service has been disposed');
      return docCache.open({ documentId, text, options, scriptScope, schemaSnapshot: cachedSnapshot });
    },
    async onTextChange(documentId, newText) {
      if (disposed) throw new Error('SqlResolverService: service has been disposed');
      return docCache.change(documentId, newText, { schemaSnapshot: cachedSnapshot });
    },
    async onApplyEdits(documentId, edits) {
      if (disposed) throw new Error('SqlResolverService: service has been disposed');
      return docCache.applyEdits(documentId, edits, { schemaSnapshot: cachedSnapshot });
    },
    onCloseDocument(documentId) {
      docCache.close(documentId);
    },
    onDDL() {
      setSnapshot(undefined);
    },
    onDisconnect() {
      disposed = true;
      unsubscribeDdl();
      docCache.clear();
      cachedSnapshot = undefined;
      pullError = undefined;
      inFlight.clear();
    },

    refreshSchemaSnapshot,
    getSchemaSnapshot() {
      return cachedSnapshot;
    },
    setSchemaSnapshot(snapshot) {
      // An explicitly replaced (or cleared) snapshot supersedes the last pull's error.
      pullError = undefined;
      setSnapshot(snapshot);
    },
    getSchemaPullError() {
      return pullError;
    },

    getParsedBuffer(documentId) {
      return docCache.get(documentId);
    },
    documentIds() {
      return docCache.documentIds();
    },

    classifyPosition(documentId, cursor) {
      return classifyPosition(requireBuffer(docCache, documentId), cursor);
    },
    resolveScope(documentId, cursor) {
      return resolveScope(requireBuffer(docCache, documentId), cursor);
    },
    resolveDottedBase(documentId, cursor) {
      return resolveDottedBase(requireBuffer(docCache, documentId), cursor);
    },
    resolveIdentifier(documentId, node) {
      return resolveIdentifier(requireBuffer(docCache, documentId), node);
    },
    suggestColumnsInScope(scope, filter) {
      return suggestColumnsInScope(scope, cachedSnapshot, filter);
    },
    suggestTablesInScope(scope, filter) {
      return suggestTablesInScope(scope, cachedSnapshot, filter);
    },
    suggestJoinCandidates(scope, leftTable) {
      return suggestJoinCandidates(scope, cachedSnapshot, leftTable);
    },
    suggestFunctionsInScope(scope, filter) {
      return suggestFunctionsInScope(scope, cachedSnapshot, filter);
    },
    suggestOperatorsForOperand(operand) {
      return suggestOperatorsForOperand(operand, engineId);
    },

    extractQueryStructure(documentId, statementId) {
      const buf = requireBuffer(docCache, documentId);
      return structureOf(buf, requireStatement(buf, statementId));
    },
    applyStructuralEdit(documentId, statementId, edit) {
      const buf = requireBuffer(docCache, documentId);
      return applyStructuralEdit(buf, requireStatement(buf, statementId), edit, { foldConditionPair });
    },
    // Range-addressable variant for VQE set-op branch editing (VQE-3 Slice 1).
    // Synthetic sub-statement ids aren't in documentStructure — bypass the id
    // lookup and pass the caller-supplied StatementInfo straight through.
    extractQueryStructureForStatement(documentId, statementInfo) {
      const buf = requireBuffer(docCache, documentId);
      return structureOf(buf, statementInfo);
    },
    applyStructuralEditForStatement(documentId, statementInfo, edit) {
      const buf = requireBuffer(docCache, documentId);
      return applyStructuralEdit(buf, statementInfo, edit, { foldConditionPair });
    },
    emitSource(documentId, modified, emitOptions) {
      const buf = requireBuffer(docCache, documentId);
      // Source the engine from the parsed buffer so comment detection gates
      // `#`-as-line-comment to MySQL only (caller-supplied emitOptions wins if
      // it sets engine explicitly).
      const opts: EmitSourceOptions = {
        engine: buf.parsedDocument.options.engine,
        ...emitOptions,
      };
      return emitSource(modified, buf.parsedDocument.source, opts);
    },

    extractDdlStructure(documentId, statementId) {
      const buf = requireBuffer(docCache, documentId);
      return extractDdlStructure(buf, requireStatement(buf, statementId));
    },
    applyDdlEdit(documentId, statementId, edit, options) {
      const buf = requireBuffer(docCache, documentId);
      return applyDdlEdit(buf, requireStatement(buf, statementId), edit, options);
    },
  };
}
